
'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import en from './dictionaries/en.json';
import es from './dictionaries/es.json';
import zh from './dictionaries/zh.json';

// El tipo, las constantes y la validacion viven en `idioma.ts`, SIN
// `'use client'`: el layout raiz corre en el servidor y necesita leer la cookie
// antes del primer pintado. Llamarlas desde aqui daba un error de frontera que
// `tsc` no caza.
import {
  COOKIE_IDIOMA,
  IDIOMA_POR_DEFECTO,
  IDIOMAS,
  esIdioma,
  type Language,
} from './idioma';

export { COOKIE_IDIOMA, IDIOMA_POR_DEFECTO, IDIOMAS, esIdioma };
export type { Language };

export type Dictionary = typeof en;

const dictionaries = { en, es, zh };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Dictionary;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({
  children,
  inicial,
}: {
  children: React.ReactNode;
  /**
   * El idioma que ya resolvio el SERVIDOR leyendo la cookie.
   *
   * Sin esto el primer pintado sale en el idioma por defecto y el de verdad
   * llega en un `useEffect`, o sea despues: se ve un fotograma en el idioma
   * equivocado en cada carga de cada pantalla. Es el mismo problema que el
   * tema y los colores de la empresa ya resuelven en el layout raiz.
   */
  inicial?: Language;
}) {
  const [language, setLanguageState] = useState<Language>(inicial ?? IDIOMA_POR_DEFECTO);

  useEffect(() => {
    // La cookie manda —es la que vio el servidor— y `localStorage` es la red
    // por debajo: cubre lo que eligieron antes de que la cookie existiera.
    if (inicial) return;

    try {
      const guardado = localStorage.getItem(COOKIE_IDIOMA);
      if (esIdioma(guardado)) setLanguageState(guardado);
    } catch {
      // Navegador con el almacenamiento bloqueado: se queda el de por defecto.
    }
  }, [inicial]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);

    try {
      localStorage.setItem(COOKIE_IDIOMA, lang);
    } catch {
      // Si no se puede guardar, el cambio vale para esta pestaña y basta.
    }

    // Y la cookie, que es la que lee el servidor en la siguiente carga.
    // `SameSite=Lax` y sin `Secure` para que valga en el localhost del muelle.
    document.cookie = `${COOKIE_IDIOMA}=${lang};path=/;max-age=31536000;SameSite=Lax`;
  };

  const value = {
    language,
    setLanguage,
    t: (dictionaries[language] || en) as Dictionary,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}
